/**
 * Embroidery / debossing add-on cart sync
 * Keeps each add-on line (e.g. Embroidery Charge) tied to the product line it belongs to:
 *  - parent removed           -> add-on removed
 *  - parent quantity changed  -> add-on quantity matched
 *  - add-on removed by itself -> personalisation text stripped from the parent line
 * Lines are linked by a shared _addon_link property. Carts created before the link existed
 * fall back to matching on _main_variant_id.
 */
(function() {
  let addonSyncBusy = false;
  let addonSyncPending = false;
  let syncTimer = null;

  const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  const CART_URL = root + 'cart.js';
  const UPDATE_URL = root + 'cart/update.js';
  const CHANGE_URL = root + 'cart/change.js';

  function scheduleSync() {
    clearTimeout(syncTimer);
    syncTimer = setTimeout(syncAddonItems, 300);
  }

  function isAddon(item) {
    const p = item.properties || {};
    return p._is_addon === 'true' || p._is_addon === true;
  }

  function onCartEvent(event) {
    if (event && event.detail && event.detail._addonSyncSource) return;
    scheduleSync();
  }

  document.addEventListener('on:cart:change', onCartEvent);
  document.addEventListener('cart:updated', onCartEvent);
  document.addEventListener('dispatch:cart-drawer:refresh', onCartEvent);

  async function postJSON(url, body) {
    const res = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
      body: JSON.stringify(body)
    });
    if (!res.ok) throw new Error('Addon sync request failed: ' + res.status + ' ' + url);
    return res.json();
  }

  async function syncAddonItems() {
    if (addonSyncBusy) {
      addonSyncPending = true;
      return;
    }
    addonSyncBusy = true;

    try {
      const res = await fetch(CART_URL, { cache: 'no-store' });
      if (!res.ok) return;
      const cart = await res.json();
      if (!cart || !cart.items || cart.items.length === 0) return;

      const mainItems = cart.items.filter(function(item) { return !isAddon(item); });
      const addonItems = cart.items.filter(isAddon);

      // 1) Add-ons: remove orphans, match quantity to parent
      const updates = {};
      addonItems.forEach(function(addon) {
        const p = addon.properties || {};
        let parentQty = 0;

        if (p._addon_link) {
          mainItems.forEach(function(item) {
            if ((item.properties || {})._addon_link === p._addon_link) parentQty += item.quantity;
          });
        } else if (p._main_variant_id) {
          mainItems.forEach(function(item) {
            if (String(item.variant_id) === String(p._main_variant_id)) parentQty += item.quantity;
          });
        } else {
          return;
        }

        if (parentQty <= 0) {
          updates[addon.key] = 0;
        } else if (addon.quantity !== parentQty) {
          updates[addon.key] = parentQty;
        }
      });

      // 2) Parents whose add-on was removed: strip the personalisation so it is not made for free
      const propertyChanges = [];
      mainItems.forEach(function(item) {
        const p = item.properties || {};
        if (!p._addon_link) return;
        const hasAddon = addonItems.some(function(addon) {
          return (addon.properties || {})._addon_link === p._addon_link;
        });
        if (hasAddon) return;

        const newProps = {};
        Object.keys(p).forEach(function(key) {
          if (key === '_addon_link' || key === '_addon_property' || key === p._addon_property) return;
          newProps[key] = p[key];
        });
        propertyChanges.push({ id: item.key, quantity: item.quantity, properties: newProps });
      });

      if (Object.keys(updates).length === 0 && propertyChanges.length === 0) return;

      let updatedCart = null;
      if (Object.keys(updates).length > 0) {
        updatedCart = await postJSON(UPDATE_URL, { updates: updates });
      }
      for (const change of propertyChanges) {
        updatedCart = await postJSON(CHANGE_URL, change);
      }

      const detail = Object.assign({}, updatedCart || {}, { _addonSyncSource: true });
      document.dispatchEvent(new CustomEvent('on:cart:change', { bubbles: true, cancelable: false, detail: detail }));
      document.dispatchEvent(new CustomEvent('cart:updated', { bubbles: true, detail: detail }));
    } catch (err) {
      console.warn('Addon cart sync error:', err);
    } finally {
      addonSyncBusy = false;
      if (addonSyncPending) {
        addonSyncPending = false;
        scheduleSync();
      }
    }
  }

  // Check on every page load, so any add-on already stranded in a basket gets cleaned up
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', scheduleSync);
  } else {
    scheduleSync();
  }
})();